import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { DimeAIThread, ChatDocument } from "../services/https/dimeai";

export type DimeAiWidgetMessage = {
  role: "user" | "assistant";
  content: string;
  // The system-appended caveat, held separately so it can render smaller
  // and dimmer instead of reading as the answer's own last sentence --
  // same split DimeAI.tsx does, see its splitCaveat helper.
  caveat?: string | null;
  // The stored row this turn corresponds to — only assistant messages get
  // one back from the server, and only those can carry a vote.
  id?: string;
  feedback?: "up" | "down" | null;
};

interface DimeAiWidgetState {
  // Whether the floating panel is expanded. Lives in Redux (not local
  // component state) specifically because the widget is mounted once at
  // the App root, above the route tree — see the comment in App.tsx on why
  // that placement matters. Redux is what lets the panel, its transcript,
  // and its in-flight thread survive a page navigation instead of
  // remounting from scratch the way anything inside MainLayout would.
  isOpen: boolean;
  messages: DimeAiWidgetMessage[];
  // Returned by the first reply and sent with every turn after it — same
  // continuation contract the full DimeAI page uses (see DimeAI.tsx),
  // since both read/write the same backend threads.
  threadId: string | null;
  // Deliberately not persisted across a "New chat" — see DimeAI.tsx's own
  // comment on why remembering the last company caused wrong-company
  // answers. Same rule applies here.
  companyId: string | null;
  needsCompany: boolean;
  awaitingReply: boolean;
  // Phase 2: past conversations, viewable/switchable from inside the
  // widget (same /ai-advisor/threads/ list the full DimeAI page reads —
  // see services/https/dimeai.ts), and files attached to the current
  // thread. Kept in Redux for the same survive-navigation reason as
  // everything else above.
  threads: DimeAIThread[];
  showHistory: boolean;
  documents: ChatDocument[];
  uploading: string | null;
  // Where the launcher/panel sits, once the user has dragged it at least
  // once — null means "use the default bottom-right CSS position." A
  // device-level placement preference, not conversation data, so unlike
  // everything else in this state it deliberately survives resetWidget()
  // below (see that reducer's comment).
  position: { right: number; bottom: number } | null;
}

const initialState: DimeAiWidgetState = {
  isOpen: false,
  messages: [],
  threadId: null,
  companyId: null,
  needsCompany: false,
  awaitingReply: false,
  threads: [],
  showHistory: false,
  documents: [],
  uploading: null,
  position: null,
};

const dimeAiWidgetSlice = createSlice({
  name: "dimeAiWidget",
  initialState,
  reducers: {
    openWidget(state) {
      state.isOpen = true;
    },
    closeWidget(state) {
      state.isOpen = false;
    },
    toggleWidget(state) {
      state.isOpen = !state.isOpen;
    },
    appendMessage(state, action: PayloadAction<DimeAiWidgetMessage>) {
      state.messages.push(action.payload);
    },
    setMessages(state, action: PayloadAction<DimeAiWidgetMessage[]>) {
      state.messages = action.payload;
    },
    setMessageFeedback(
      state,
      action: PayloadAction<{ id: string; feedback: "up" | "down" | null }>,
    ) {
      const message = state.messages.find((m) => m.id === action.payload.id);
      if (message) message.feedback = action.payload.feedback;
    },
    setThreadId(state, action: PayloadAction<string | null>) {
      state.threadId = action.payload;
    },
    setCompanyId(state, action: PayloadAction<string | null>) {
      state.companyId = action.payload;
    },
    setNeedsCompany(state, action: PayloadAction<boolean>) {
      state.needsCompany = action.payload;
    },
    setAwaitingReply(state, action: PayloadAction<boolean>) {
      state.awaitingReply = action.payload;
    },
    // New chat: drop the transcript, thread and company together. Clearing
    // only one leaves the assistant answering from context the user can no
    // longer see, or under a company they did not pick for this
    // conversation — same reasoning as DimeAI.tsx's own "New chat" handler.
    // Attachments go with it too: a new thread has none yet, same as the
    // full page's own threadId-driven documents effect.
    resetWidgetConversation(state) {
      state.messages = [];
      state.threadId = null;
      state.companyId = null;
      state.needsCompany = false;
      state.awaitingReply = false;
      state.documents = [];
    },
    setThreads(state, action: PayloadAction<DimeAIThread[]>) {
      state.threads = action.payload;
    },
    setShowHistory(state, action: PayloadAction<boolean>) {
      state.showHistory = action.payload;
    },
    toggleHistory(state) {
      state.showHistory = !state.showHistory;
    },
    setDocuments(state, action: PayloadAction<ChatDocument[]>) {
      state.documents = action.payload;
    },
    addDocuments(state, action: PayloadAction<ChatDocument[]>) {
      state.documents.push(...action.payload);
    },
    removeDocument(state, action: PayloadAction<string>) {
      state.documents = state.documents.filter((d) => d.id !== action.payload);
    },
    setUploading(state, action: PayloadAction<string | null>) {
      state.uploading = action.payload;
    },
    setWidgetPosition(
      state,
      action: PayloadAction<{ right: number; bottom: number } | null>,
    ) {
      state.position = action.payload;
    },
    // Hard boundary for a login/logout — not the same thing as "New chat"
    // (resetWidgetConversation above). That one intentionally keeps threads,
    // isOpen and showHistory, because it's the same user picking a fresh
    // conversation. This one is for when the *identity* behind the widget
    // has changed: without it, one person's messages, thread list and
    // attached documents stay sitting in this slice — which lives at the
    // App root and outlives any single page — and are then shown to
    // whoever logs into the same browser tab next. Everything goes back to
    // initialState except `position`: where someone likes the launcher
    // parked is a browser/device preference, not that person's data, so it
    // has no privacy reason to reset with the rest.
    resetWidget(state) {
      const { position } = state;
      Object.assign(state, initialState, { position });
    },
  },
});

export const {
  openWidget,
  closeWidget,
  toggleWidget,
  appendMessage,
  setMessages,
  setMessageFeedback,
  setThreadId,
  setCompanyId,
  setNeedsCompany,
  setAwaitingReply,
  resetWidgetConversation,
  setThreads,
  setShowHistory,
  toggleHistory,
  setDocuments,
  addDocuments,
  removeDocument,
  setUploading,
  setWidgetPosition,
  resetWidget,
} = dimeAiWidgetSlice.actions;

export default dimeAiWidgetSlice.reducer;
